import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { TrendingUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created. Welcome!");
      navigate("/choose-plan");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#090D16] text-slate-100">
      <div className="hidden lg:flex flex-col justify-between p-12 grid-bg border-r border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 grid place-items-center glow-primary">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <span className="font-display font-extrabold text-xl">Trading Academy</span>
        </div>
        <div className="max-w-md">
          <h1 className="font-display text-5xl font-black leading-tight mb-4">Start with<br />$100,000 virtual.</h1>
          <p className="text-slate-400 text-lg">15 modules, interactive charts, quizzes and a full paper-trading simulator. Learn safely, risk nothing.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-amber-400/80">
          <AlertTriangle className="w-4 h-4" /> Educational only. No real money, no profits.
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="register-form">
          <div>
            <h2 className="font-display text-2xl font-bold">Create your account</h2>
            <p className="text-slate-400 text-sm mt-1">It's free — start learning in seconds.</p>
          </div>
          {error && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="register-error">{error}</div>}
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Name</label>
            <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none" placeholder="Your name" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Email</label>
            <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none" placeholder="you@example.com" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Password</label>
            <input data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none" placeholder="At least 6 characters" />
          </div>
          <button data-testid="register-submit" disabled={loading} type="submit"
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold rounded-lg py-2.5 transition-colors">
            {loading ? "Creating…" : "Create account"}
          </button>
          <p className="text-sm text-slate-400 text-center">Already have an account? <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium" data-testid="go-login">Sign in</Link></p>
        </form>
      </div>
    </div>
  );
}
